import { singleline } from "@core/foundation/string";
import { apiEndpoint, publicApi } from "@core/ports/http";
import { z } from "@core/zod";

import { PROJECT_DOMAIN } from "@platform/core";

import { ACCOUNT_SERVICE } from "../config/index.ts";
import { OAuthClientIdSchema, OAuthGrantTypeSchema } from "../models/index.ts";

// oxlint-disable-next-line no-warning-comments
// HOTSPOT: Ensures minimal PKCE entropy complexity
const CODE_VERIFIER_MIN_LENGTH = 43;

export const oauthPublicApi = publicApi(
  { domain: `oauth.${ACCOUNT_SERVICE.LABEL}.${PROJECT_DOMAIN}`, path: "" },
  {
    ".well-known/oauth-authorization-server": apiEndpoint("GET", {
      response: z.object(
        {
          authorization_endpoint: z.url(),
          code_challenge_methods_supported: z.array(z.string()),
          grant_types_supported: z.array(z.string()),
          issuer: z.url(),
          registration_endpoint: z.url(),
          response_types_supported: z.array(z.string()),
          revocation_endpoint: z.url(),
          scopes_supported: z.array(z.string()),
          token_endpoint: z.url(),
          token_endpoint_auth_methods_supported: z.array(z.string()),
        },
        {
          authorization_endpoint: "The URL of the authorize endpoint",
          code_challenge_methods_supported:
            "The PKCE code challenge methods the server accepts",
          grant_types_supported: "The OAuth grant types the server accepts",
          issuer: "The server's issuer, the origin of its endpoints",
          registration_endpoint: "The URL of the client registration endpoint",
          response_types_supported:
            "The response types the authorize endpoint accepts",
          revocation_endpoint: "The URL of the token revocation endpoint",
          scopes_supported: "The scopes a client may request",
          token_endpoint: "The URL of the token endpoint",
          token_endpoint_auth_methods_supported:
            "The methods a client may authenticate to the token endpoint with",
        },
      ),
    }),
    "authorize": apiEndpoint("GET", {
      queryParams: z.object(
        {
          client_id: OAuthClientIdSchema,
          code_challenge: z.string(),
          code_challenge_method: z.literal("S256"),
          redirect_uri: z.url(),
          resource: z.url(),
          response_type: z.literal("code"),
          scope: z.string().optional(),
          state: z.string().optional(),
        },
        {
          client_id: "The id of the client requesting the authorization",
          code_challenge: "The client's PKCE code challenge",
          code_challenge_method:
            "The method the code challenge was derived with",
          redirect_uri: "The URI the browser returns to with the result",
          resource: "The URL of the resource server the token is requested for",
          response_type: "The response type the client requests",
          scope: "The scopes the client requests, separated by spaces",
          state: "The client's opaque state, returned with the result",
        },
      ),
      response: z.void(),
    }),
    "register": apiEndpoint({
      body: z.object(
        {
          client_name: z.string(),
          grant_types: z.array(OAuthGrantTypeSchema).optional(),
          redirect_uris: z.array(z.url()).nonempty(),
          token_endpoint_auth_method: z.literal("none").optional(),
        },
        {
          client_name: "The name the client registers under",
          grant_types: singleline`
            The OAuth grant types the client may use, every supported type when
            omitted
          `,
          redirect_uris: "The redirect URIs the client registers",
          token_endpoint_auth_method:
            "The method the client authenticates to the token endpoint with",
        },
      ),
      response: z.object(
        {
          client_id: OAuthClientIdSchema,
          client_name: z.string(),
          grant_types: z.array(OAuthGrantTypeSchema),
          redirect_uris: z.array(z.url()),
          token_endpoint_auth_method: z.literal("none"),
        },
        {
          client_id: "The client id, derived from the registration",
          client_name: "The name the client registered under",
          grant_types: "The OAuth grant types the client may use",
          redirect_uris: "The redirect URIs the client registered",
          token_endpoint_auth_method:
            "The method the client authenticates to the token endpoint with",
        },
      ),
    }),
    "revoke": apiEndpoint({
      body: z.object(
        {
          client_id: OAuthClientIdSchema,
          token: z.string(),
          token_type_hint: z.string().optional(),
        },
        {
          client_id: "The id of the client the token was issued to",
          token: "The refresh token to revoke",
          token_type_hint: "The client's hint of the token's type, unused",
        },
      ),
      response: z.void(),
    }),
    "token": apiEndpoint({
      body: z.discriminatedUnion("grant_type", [
        z.object(
          {
            client_id: OAuthClientIdSchema,
            code: z.string(),
            code_verifier: z.string().min(CODE_VERIFIER_MIN_LENGTH),
            grant_type: z.literal("authorization_code"),
            redirect_uri: z.url(),
          },
          {
            client_id: "The id of the client the code was issued to",
            code: "The authorization code to exchange",
            code_verifier: "The PKCE code verifier behind the code challenge",
            grant_type: "The grant type, exchanging an authorization code",
            redirect_uri:
              "The redirect URI the authorization was requested with",
          },
        ),
        z.object(
          {
            client_id: OAuthClientIdSchema,
            grant_type: z.literal("refresh_token"),
            refresh_token: z.string(),
          },
          {
            client_id: "The id of the client the refresh token was issued to",
            grant_type: "The grant type, exchanging a refresh token",
            refresh_token: "The refresh token to exchange",
          },
        ),
      ]),
      response: z.object(
        {
          access_token: z.string(),
          expires_in: z.int().positive(),
          refresh_token: z.string(),
          scope: z.string().optional(),
          token_type: z.literal("Bearer"),
        },
        {
          access_token: "The access token issued to the client",
          expires_in: "The access token's lifetime, in seconds",
          refresh_token: "The refresh token that renews the access token",
          scope: "The scopes the access token grants, separated by spaces",
          token_type: "The access token's type",
        },
      ),
    }),
  },
);
